import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';

import {
  ArrowForward,
  ArrowBack as ArrowBackIcon,
  CheckCircle,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminService } from '../services/admin';
import { useAuth } from '../context/AuthContext';
import { decodeToken, getToken } from '../utils/auth';
import {
  getC,
  detailsSchema,
  settingsSchema,
  domainSchema,
  ALL_STEPS,
  emptyDomain,
  Step1Details,
  Step2Domains,
  Step3Security,
  Step4Review,
  isBusinessLocked,
  isBusinessInactive,
  PreviewSidebar,
} from './ApplicationWizardSteps';
import useFormPersist from '../hooks/useFormPersist';

// Storage key for the "Create Application" wizard draft (see useFormPersist).
// Only used in create mode — edit mode is seeded from the real app.
const APP_DRAFT_KEY = 'goflipo:draft:create-application';
// Edit mode keeps its pending (not yet committed) changes in the browser too,
// one draft per application. Nothing reaches the database until "Done".
const editDraftKey = (id) => `goflipo:draft:edit-application:${id}`;

const cloneValues = (v) => JSON.parse(JSON.stringify(v));

// Payload for a single domain row — shared by create/update/add calls.
const domainPayload = (d) => ({
  domainName: (d.domain || '').trim(),
  displayName: (d.displayName || '').trim(),
  redirectUrl: d.redirectUrl,
  logoUrl: d.logoUrl || undefined,
  enabled: d.status,
});

/* ----------------------------------------------------------------------
   ApplicationWizard — the single engine behind both the Create and Edit
   application pages. Both pages render the exact same steps in the same
   order; the only difference is what they start with:

     mode='create': appId starts null, domains start empty, settings
       haven't been saved yet — every step only validates and keeps its
       values in the browser (formik + sessionStorage draft); the app is
       written to the database once, on the final "Create Application".

     mode='edit': appId, formValues, and settingsSaved are all passed in
       already-populated from the backend by the caller (EditApplicationPage
       fetches the app/domains/settings first). Like create mode, the steps
       (including "Save Domain", domain delete and the status toggle) only
       change local state, mirrored to a localStorage draft for this app.
       The database is updated once, on the final "Done": it diffs the form
       against the values loaded from the backend and sends only the
       differences (app details, settings, domain deletes/updates/adds).
       Each domain loaded from the backend carries `savedDomainName` (the
       name it is stored under); a row without one is new.
---------------------------------------------------------------------- */
const ApplicationWizard = ({
  mode = 'create', // 'create' | 'edit'
  initialAppId = null,
  initialFormValues,
  initialSettingsSaved = false,
  initialBusinessStatus = null, // edit mode: the owning business's accountStatus
  initialVersion = null, // app.updatedAt at load time — detects a stale edit draft
  onDone,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user, email: authEmail } = useAuth();
  // A business user's own businesses._id (the id the backend stores on apps).
  // Fall back to the token for sessions stored before `id` was saved on login.
  const userBusinessId = user?.id || (!isAdmin ? decodeToken(getToken())?.id : undefined);

  const createdByEmail =
    user?.email || authEmail || user?.username || user?.name || 'Unknown user';

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [fetchingBusinesses, setFetchingBusinesses] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [appId, setAppId] = useState(initialAppId);
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(initialSettingsSaved);
  const [expandedDomains, setExpandedDomains] = useState(
    mode === 'edit' ? {} : { 0: true } // edit: start with saved domains collapsed
  );
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null });
  const [domainErrors, setDomainErrors] = useState({});
  const [domainTouched, setDomainTouched] = useState({});
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [exitSaving, setExitSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Edit mode: names (as stored in the DB) of domains removed locally; the
  // DELETE calls are sent on "Done".
  const [deletedDomains, setDeletedDomains] = useState([]);
  const [staleDraft, setStaleDraft] = useState(false);

  const preselectedBusinessId = new URLSearchParams(location.search).get('businessId');

  // Combine the per-step schemas so Formik's own validation runs on every
  // change/blur across the whole wizard (same live-error behavior as the
  // "Create Business" form), while saveDetails/saveSettings/handleCreate
  // below still validate against the individual schemas to gate step
  // navigation and surface the right banner message per step.
  const combinedSchema = Yup.object({ ...detailsSchema.fields, ...settingsSchema.fields });

  const formik = useFormik({
    initialValues:
      initialFormValues || {
        businessId: preselectedBusinessId || (isAdmin ? '' : userBusinessId || ''),
        applicationType: 'WEB',
        description: '',
        businessName: '',
        isActive: true,
        domains: [emptyDomain()],
        blockDuration: 30,
        failedAttempts: 5,
        ipGeolocationEnabled: false,
        webhookUrl: '',
        metadataJson: '{\n  "placeholder": true\n}',
        appLogoUrl: '',
      },
    validationSchema: combinedSchema,
    validateOnChange: true,
  });

  const { values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched } = formik;

  // Edit mode: what the database currently holds (as loaded), used by "Done"
  // to work out what actually changed. Updated after each successful call so
  // a retry after a partial failure only re-sends what is still outstanding.
  const baselineRef = useRef(null);
  if (mode === 'edit' && !baselineRef.current && initialFormValues) {
    const base = cloneValues(initialFormValues);
    baselineRef.current = {
      values: base,
      settingsSaved: initialSettingsSaved,
      domains: Object.fromEntries(
        (base.domains || []).filter((d) => d.savedDomainName).map((d) => [d.savedDomainName, d])
      ),
    };
  }

  // Yup's own validate() throws an error whose `.inner` holds one entry per
  // invalid field (path + message). Formik's `errors`/`touched` state only
  // drives the inline per-field messages when something actually writes into
  // it, so every manual `.validate()` catch block below runs its result
  // through here instead of just showing one generic banner.
  const applyYupErrors = (err) => {
    const entries = err.inner && err.inner.length ? err.inner : [err];
    const fieldErrors = {};
    const touchedFields = {};
    entries.forEach((e) => {
      if (e.path) {
        fieldErrors[e.path] = e.message;
        touchedFields[e.path] = true;
      }
    });
    formik.setTouched({ ...formik.touched, ...touchedFields }, false);
    formik.setErrors({ ...formik.errors, ...fieldErrors });
  };

  // Auto-save the whole wizard (all field values + which step you're on) so a
  // refresh or navigating away doesn't lose anything. Create mode only — edit
  // mode is populated from the existing app. The draft is cleared on a
  // successful create or when the user discards (see handleCreate /
  // handleDiscardAndLeave below).
  const { clearDraft: clearAppDraft, restored: draftRestored } = useFormPersist(
    mode === 'edit' ? editDraftKey(initialAppId) : APP_DRAFT_KEY,
    formik,
    {
      enabled: true,
      // create: per-tab sessionStorage (default); edit: localStorage so pending
      // edits survive closing the browser.
      storage:
        mode === 'edit' && typeof window !== 'undefined' ? window.localStorage : undefined,
      // Edit drafts must also remember pending domain deletions, which are
      // not part of the formik values (a deletion alone doesn't make the
      // form "dirty", so it is folded into the draft below).
      extra: { step: activeStep, deletedDomains, version: initialVersion },
      forceSave: mode === 'edit' && deletedDomains.length > 0,
      onRestoreExtra: (saved) => {
        const step = typeof saved === 'number' ? saved : saved?.step;
        if (typeof step === 'number' && step >= 0) setActiveStep(step);
        if (saved && typeof saved === 'object') {
          if (Array.isArray(saved.deletedDomains)) setDeletedDomains(saved.deletedDomains);
          if (mode === 'edit' && saved.version && initialVersion && saved.version !== initialVersion) {
            setStaleDraft(true);
          }
        }
      },
    }
  );
  const hasPendingChanges = formik.dirty || deletedDomains.length > 0;

  // Fetch businesses if admin (needed in both modes so Step1 can show the
  // business name next to the id; the Autocomplete itself is disabled in
  // edit mode via lockBusiness below).
  useEffect(() => {
    if (isAdmin) {
      setFetchingBusinesses(true);
      adminService
        .getAllBusinesses(0, 100)
        .then((data) => {
          let list = [];
          if (Array.isArray(data)) list = data;
          else if (data.businesses && Array.isArray(data.businesses)) list = data.businesses;
          else if (data.data && Array.isArray(data.data)) list = data.data;
          setBusinesses(list);
        })
        .catch(() => {})
        .finally(() => setFetchingBusinesses(false));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (mode === 'create' && !isAdmin && userBusinessId) {
      setFieldValue('businessId', userBusinessId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, userBusinessId, mode]);

  // Applications of an INACTIVE business can't be enabled, and new ones can't
  // be created for it (the backend enforces both; this just mirrors it in the UI).
  const selectedBusiness = businesses.find((b) => b._id === values.businessId) || null;
  const businessInactive =
    mode === 'edit'
      ? String(initialBusinessStatus || '').toUpperCase() === 'INACTIVE'
      : isBusinessInactive(selectedBusiness);

  const showDomains = values.applicationType === 'WEB';

  const visibleSteps = ALL_STEPS.filter((s) => s.key !== 'domains' || showDomains);
  const currentStepKey = visibleSteps[activeStep]?.key;

  const goNextIndex = () => setActiveStep((i) => Math.min(i + 1, visibleSteps.length - 1));
  const goPrevIndex = () => setActiveStep((i) => Math.max(i - 1, 0));

  // Safety: a restored draft step (or switching WEB→MOBILE, which drops the
  // Domains step) must never point past the last visible step.
  useEffect(() => {
    if (activeStep > visibleSteps.length - 1) {
      setActiveStep(visibleSteps.length - 1);
    }
  }, [visibleSteps.length, activeStep]);

  // Whenever the Domains step becomes active — moving forward into it,
  // coming Back to it, or jumping to it via "Edit" on Review — only the
  // LAST domain row should start expanded; every other row collapses.
  // Applies the same way in create and edit mode.
  useEffect(() => {
    if (currentStepKey !== 'domains') return;
    const lastIndex = values.domains.length - 1;
    setExpandedDomains(() => {
      const next = {};
      values.domains.forEach((_, i) => { next[i] = i === lastIndex; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey]);

  /* ---------------- API calls ---------------- */

  // STEP 1 — validate only; values stay in local (formik) state and are
  //   mirrored to a browser draft by useFormPersist.
  const saveDetails = async ({ advance = true } = {}) => {
    try {
      await detailsSchema.validate(values, { abortEarly: false });
    } catch (err) {
      applyYupErrors(err);
      return false;
    }

    // Nothing is written to the database here (create or edit): Step 1 only
    // validates and keeps the values in local (formik) state, which is
    // mirrored to a browser draft. They are committed on the final
    // "Create Application" / "Done" click.
    setSubmitError('');
    if (advance) goNextIndex();
    return true;
  };

  // Validate only; settings are persisted on the final "Create Application" /
  // "Done" click, not now.
  const saveSettings = async ({ advance = true } = {}) => {
    try {
      await settingsSchema.validate(values, { abortEarly: false });
    } catch (err) {
      applyYupErrors(err);
      return false;
    }
    setSubmitError('');
    if (mode === 'create') setSettingsSaved(true);
    if (advance) goNextIndex();
    return true;
  };

  /* ---------------- Domain handlers ---------------- */
  const addDomainRow = () => {
    const next = [...values.domains, emptyDomain()];
    setFieldValue('domains', next);
    setExpandedDomains((prev) => ({ ...prev, [next.length - 1]: true }));
  };

 const updateDomainField = (index, field, value) => {
  const updated = values.domains.map((d, i) =>
    i === index ? { ...d, [field]: value, isDirty: true } : d
  );
  setFieldValue('domains', updated);
  setDomainErrors((prev) => {
    if (!prev[index]?.[field]) return prev;
    const nextRow = { ...prev[index] };
    delete nextRow[field];
    return { ...prev, [index]: nextRow };
  });
};

const markDomainFieldTouched = (index, field) => {
  setDomainTouched((prev) => ({
    ...prev,
    [index]: { ...prev[index], [field]: true },
  }));
};

const validateDomainRow = (index) => {
  try {
    domainSchema.validateSync(values.domains[index], { abortEarly: false });

    // A domain must be unique within this application — the backend enforces
    // the same rule (businessAppService.addDomain: "This domain is already
    // added to the app"). Catching it here means it's flagged inline on
    // THIS step, instead of only surfacing later when the app is actually
    // created/saved (which would show up as a generic error on Review).
    const normalized = (values.domains[index].domain || '').trim().toLowerCase();
    const isDuplicate = normalized && values.domains.some((d, i) =>
      i !== index && (d.domain || '').trim().toLowerCase() === normalized
    );
    if (isDuplicate) {
      const rowErrors = { domain: 'This domain has already been added to this application.' };
      setDomainErrors((prev) => ({ ...prev, [index]: rowErrors }));
      setDomainTouched((prev) => ({ ...prev, [index]: { ...prev[index], domain: true } }));
      return false;
    }

    setDomainErrors((prev) => ({ ...prev, [index]: {} }));
    return true;
  } catch (err) {
    const rowErrors = {};
    const rowTouched = {};
    (err.inner && err.inner.length ? err.inner : [err]).forEach((e) => {
      if (e.path) {
        rowErrors[e.path] = e.message;
        rowTouched[e.path] = true;
      }
    });
    setDomainErrors((prev) => ({ ...prev, [index]: rowErrors }));
    setDomainTouched((prev) => ({ ...prev, [index]: { ...prev[index], ...rowTouched } }));
    return false;
  }
};

  // "Save Domain" — validates the row and marks it saved in local state only.
  // Create mode persists all domains with the app; edit mode persists the
  // differences on "Done". A row that already exists in the database keeps
  // its `savedDomainName` (the name it is stored under, needed to update or
  // delete it); a brand-new row has none.
  const saveDomain = async (index) => {
    const domain = values.domains[index];
    if (!validateDomainRow(index)) {
      return false;
    }
    setSubmitError('');
    const updated = values.domains.map((d, i) =>
      i === index
        ? {
            ...d,
            id: true,
            savedDomainName: mode === 'create' ? domain.domain : d.savedDomainName || null,
            saved: true,
            isDirty: false,
          }
        : d
    );
    setFieldValue('domains', updated);
    setExpandedDomains((prev) => ({ ...prev, [index]: false }));
    return true;
  };

const removeDomain = (index) => {
  const domain = values.domains[index];
  if (!domain.saved) {
    const updated = values.domains.filter((_, i) => i !== index);
    setFieldValue('domains', updated.length ? updated : [emptyDomain()]);
    return;
  }
  setDeleteConfirm({ open: true, index });
};

// Removes the row locally. In edit mode, a domain that exists in the database
// is remembered in `deletedDomains` and deleted on "Done".
const confirmDeleteDomain = () => {
  const { index } = deleteConfirm;
  if (index === null) return;
  const domain = values.domains[index];
  if (mode === 'edit' && domain.savedDomainName) {
    setDeletedDomains((prev) =>
      prev.includes(domain.savedDomainName) ? prev : [...prev, domain.savedDomainName]
    );
  }
  const updated = values.domains.filter((_, i) => i !== index);
  setFieldValue('domains', updated.length ? updated : [emptyDomain()]);
  setDeleteConfirm({ open: false, index: null });
};

// Local only — the new status is committed with everything else on "Done".
const toggleDomainStatus = (index) => {
  const updated = values.domains.map((d, i) =>
    i === index ? { ...d, status: !d.status } : d
  );
  setFieldValue('domains', updated);
};

  const handleNext = async () => {
    if (currentStepKey === 'details') {
      await saveDetails();
    } else if (currentStepKey === 'domains') {
      const unsavedIndexes = values.domains
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => !d.saved && d.domain.trim())
        .map(({ i }) => i);
      if (unsavedIndexes.length > 0) {
        setSubmitError('Please save all domains before proceeding.');
        // Surface the same red per-field outlines "Save Domain" would show,
        // and make sure the offending row is actually expanded — matches the
        // behavior when there's no saved domain at all (below), instead of
        // only showing the generic banner for a second/third unsaved domain.
        unsavedIndexes.forEach((i) => validateDomainRow(i));
        setExpandedDomains((prev) => {
          const next = { ...prev };
          unsavedIndexes.forEach((i) => { next[i] = true; });
          return next;
        });
        return;
      }
      // At least one domain must actually be saved before moving on — an
      // all-blank row was previously excluded from `unsaved` above (nothing
      // typed to save) and silently let the step advance with zero domains.
      // Surface the same per-field errors "Save Domain" would instead.
      const hasSavedDomain = values.domains.some((d) => d.saved);
      if (!hasSavedDomain) {
        values.domains.forEach((_, i) => validateDomainRow(i));
        setExpandedDomains((prev) => ({ ...prev, 0: true }));
        return;
      }

      // Re-validate every filled row (including already-saved ones) —
      // catches a saved row that ended up a duplicate of another (or
      // otherwise invalid) without being re-saved through "Save Domain".
      // Surfacing that here keeps the error on THIS step instead of only
      // showing up later when the app is actually created.
      let allRowsValid = true;
      const invalidIndexes = [];
      values.domains.forEach((d, i) => {
        if (!d.domain || !d.domain.trim()) return;
        if (!validateDomainRow(i)) {
          allRowsValid = false;
          invalidIndexes.push(i);
        }
      });
      if (!allRowsValid) {
        setSubmitError('Please fix the highlighted domain(s) before proceeding.');
        setExpandedDomains((prev) => {
          const next = { ...prev };
          invalidIndexes.forEach((i) => { next[i] = true; });
          return next;
        });
        return;
      }

      // Drop any blank, never-saved draft rows (e.g. an empty "+ Add another
      // domain" row the user opened but never filled in) so only domains
      // that were actually saved carry forward — into Review, and back into
      // this step if the user returns to it later.
      const keepIndexes = [];
      const savedOnly = values.domains.filter((d, i) => {
        if (d.saved) { keepIndexes.push(i); return true; }
        return false;
      });
      if (savedOnly.length !== values.domains.length) {
        const remap = (prev) => {
          const next = {};
          keepIndexes.forEach((oldIdx, newIdx) => {
            if (prev[oldIdx] !== undefined) next[newIdx] = prev[oldIdx];
          });
          return next;
        };
        setFieldValue('domains', savedOnly);
        setExpandedDomains(remap);
        setDomainErrors(remap);
        setDomainTouched(remap);
      }

      goNextIndex();
    } else if (currentStepKey === 'settings') {
      await saveSettings();
    } else {
      goNextIndex();
    }
  };

  const handleBack = () => goPrevIndex();

  const applicationsListPath = isAdmin ? '/admin/applications' : '/business/applications';

  const handleDone = () => {
    if (onDone) {
      onDone(appId);
    } else {
      navigate(applicationsListPath);
    }
  };

  // EDIT mode: this is the ONE point where changes reach the database. The
  // form is compared with what was loaded from the backend and only the
  // differences are sent: app details, settings, then domain deletes,
  // updates and adds (deletes first so a removed name can be re-added).
  // If a call fails the wizard stays open with the error; the baseline is
  // advanced after each success, so retrying "Done" only re-sends what is
  // still outstanding.
  const commitEdit = async () => {
    try {
      await detailsSchema.validate(values, { abortEarly: false });
      await settingsSchema.validate(values, { abortEarly: false });
    } catch (err) {
      applyYupErrors(err);
      setSubmitError(err.errors?.[0] || 'Please complete all required fields before saving.');
      return;
    }

    let metadataObj = {};
    if (values.metadataJson && values.metadataJson.trim()) {
      try {
        metadataObj = JSON.parse(values.metadataJson);
      } catch {
        setSubmitError('Metadata must be valid JSON.');
        return;
      }
    }

    const rows = showDomains ? values.domains.filter((d) => d.domain && d.domain.trim()) : [];
    if (showDomains) {
      const bad = [];
      values.domains.forEach((d, i) => {
        if (d.domain && d.domain.trim() && !validateDomainRow(i)) bad.push(i);
      });
      if (bad.length) {
        setSubmitError('Please fix the highlighted domain(s) before saving.');
        setExpandedDomains((prev) => {
          const next = { ...prev };
          bad.forEach((i) => { next[i] = true; });
          return next;
        });
        const di = visibleSteps.findIndex((st) => st.key === 'domains');
        if (di >= 0) setActiveStep(di);
        return;
      }
      if (rows.length === 0) {
        setSubmitError('At least one domain is required.');
        return;
      }
    }

    const base = baselineRef.current;
    const settingsPayload = (v, meta) => ({
      blockDuration: `${v.blockDuration} minutes`,
      failedAttemptsBeforeBlock: v.failedAttempts,
      webhookUrl: v.webhookUrl || undefined,
      // ipGeolocationEnabled: v.ipGeolocationEnabled, // IP Allowlisting — disabled for now
      metadata: meta,
      enabled: v.isActive,
    });
    const parseMeta = (v) => {
      try { return JSON.parse(v.metadataJson || '{}'); } catch { return {}; }
    };

    setSubmitting(true);
    setSubmitError('');
    const finalRows = values.domains.map((d) => ({ ...d }));
    let failedRowIndex = null;
    let progressed = false;
    try {
      // 1. App details
      const detailsNow = {
        appName: values.businessName,
        appDescription: values.description,
        appLogoUrl: values.appLogoUrl || '',
      };
      const detailsBase = {
        appName: base.values.businessName,
        appDescription: base.values.description,
        appLogoUrl: base.values.appLogoUrl || '',
      };
      if (JSON.stringify(detailsNow) !== JSON.stringify(detailsBase)) {
        await adminService.updateBusinessApp(appId, detailsNow);
        base.values = { ...base.values, businessName: values.businessName, description: values.description, appLogoUrl: values.appLogoUrl };
        progressed = true;
      }

      // 2. Settings
      const nowSettings = settingsPayload(values, metadataObj);
      if (
        !base.settingsSaved ||
        JSON.stringify(nowSettings) !== JSON.stringify(settingsPayload(base.values, parseMeta(base.values)))
      ) {
        if (base.settingsSaved) {
          await adminService.updateAppSettings(appId, nowSettings);
        } else {
          await adminService.addAppSettings(appId, nowSettings);
          base.settingsSaved = true;
          setSettingsSaved(true);
        }
        base.values = {
          ...base.values,
          blockDuration: values.blockDuration,
          failedAttempts: values.failedAttempts,
          webhookUrl: values.webhookUrl,
          metadataJson: values.metadataJson,
          isActive: values.isActive,
        };
        progressed = true;
      }

      // 3. Domains
      if (showDomains) {
        for (const name of [...deletedDomains]) {
          // eslint-disable-next-line no-await-in-loop
          await adminService.deleteAppDomain(name, appId);
          delete base.domains[name];
          setDeletedDomains((prev) => prev.filter((n) => n !== name));
          progressed = true;
        }
        for (let i = 0; i < values.domains.length; i += 1) {
          const d = values.domains[i];
          if (!d.domain || !d.domain.trim()) continue;
          const payload = domainPayload(d);
          const before = d.savedDomainName ? base.domains[d.savedDomainName] : null;
          if (before) {
            const changed =
              JSON.stringify(payload) !== JSON.stringify(domainPayload(before));
            if (changed) {
              failedRowIndex = i;
              // eslint-disable-next-line no-await-in-loop
              await adminService.updateAppDomain(d.savedDomainName, appId, payload);
              delete base.domains[d.savedDomainName];
              base.domains[payload.domainName] = { ...d, savedDomainName: payload.domainName };
              finalRows[i] = { ...d, savedDomainName: payload.domainName, isDirty: false };
              progressed = true;
            }
          } else {
            failedRowIndex = i;
            // eslint-disable-next-line no-await-in-loop
            await adminService.addAppDomain(appId, payload);
            base.domains[payload.domainName] = { ...d, savedDomainName: payload.domainName };
            finalRows[i] = { ...d, savedDomainName: payload.domainName, saved: true, isDirty: false };
            progressed = true;
          }
          failedRowIndex = null;
        }
      }

      clearAppDraft();
      handleDone();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to save changes';
      setSubmitError(message);
      if (failedRowIndex !== null && /domain/i.test(message)) {
        setDomainErrors((prev) => ({ ...prev, [failedRowIndex]: { ...prev[failedRowIndex], domain: message } }));
        setDomainTouched((prev) => ({ ...prev, [failedRowIndex]: { ...prev[failedRowIndex], domain: true } }));
        setExpandedDomains((prev) => ({ ...prev, [failedRowIndex]: true }));
        const di = visibleSteps.findIndex((st) => st.key === 'domains');
        if (di >= 0) setActiveStep(di);
      }
      if (progressed) setFieldValue('domains', finalRows);
    } finally {
      setSubmitting(false);
    }
  };

  // CREATE mode: this is the ONE point where anything is written to the
  // database. The whole app (details + domains + settings) is created in a
  // single atomic backend call; if it fails, nothing is persisted and the
  // user stays on the wizard with the error shown.
  const handleCreate = async () => {
    if (mode !== 'create') {
      await commitEdit();
      return;
    }

    if (isBusinessLocked(selectedBusiness)) {
      setSubmitError(
        selectedBusiness.isBlocked
          ? 'This business is blocked and cannot create new applications.'
          : 'This business is inactive and cannot create new applications. Activate the business first.'
      );
      return;
    }

    // Re-validate the two form-backed steps before committing.
    try {
      await detailsSchema.validate(values, { abortEarly: false });
      await settingsSchema.validate(values, { abortEarly: false });
    } catch (err) {
      applyYupErrors(err);
      setSubmitError(err.errors?.[0] || 'Please complete all required fields before creating.');
      return;
    }

    let metadataObj = {};
    if (values.metadataJson && values.metadataJson.trim()) {
      try {
        metadataObj = JSON.parse(values.metadataJson);
      } catch {
        setSubmitError('Metadata must be valid JSON.');
        return;
      }
    }

    const domainsPayload = showDomains
      ? values.domains
          .filter((d) => d.domain && d.domain.trim())
          .map((d) => ({
            domainName: d.domain.trim(),
            displayName: (d.displayName || '').trim(),
            redirectUrl: d.redirectUrl,
            logoUrl: d.logoUrl || undefined,
            enabled: d.status,
          }))
      : [];

    const payload = {
      businessId: values.businessId,
      appName: values.businessName,
      appDescription: values.description,
      appType: values.applicationType.toLowerCase(), // 'web' | 'mobile'
      domains: domainsPayload,
      settings: {
        blockDuration: `${values.blockDuration} minutes`,
        failedAttemptsBeforeBlock: values.failedAttempts,
        webhookUrl: values.webhookUrl || undefined,
        appLogoUrl: values.appLogoUrl || undefined,
        // ipGeolocationEnabled: values.ipGeolocationEnabled, // IP Allowlisting — disabled for now
        metadata: metadataObj,
        enabled: values.isActive,
      },
    };

    setSubmitting(true);
    setSubmitError('');
    try {
      const response = await adminService.createBusinessAppFull(payload);
      const newAppId =
        response?._id ||
        response?.id ||
        response?.data?._id ||
        response?.data?.appId ||
        response?.data?.id;
      // Created successfully — discard the saved draft so the next new app
      // starts blank.
      clearAppDraft();
      if (onDone) {
        onDone(newAppId);
      } else {
        navigate(applicationsListPath);
      }
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || err.message || 'Failed to create application'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Breadcrumb "Applications" link — leaves the wizard. If the form is
  // untouched (formik.dirty === false) there's nothing to lose, so go
  // straight to the list; otherwise ask first instead of silently
  // discarding whatever the user typed.
  const handleApplicationsBreadcrumbClick = (e) => {
    e.preventDefault();
    if (hasPendingChanges) {
      setExitConfirmOpen(true);
    } else {
      navigate(applicationsListPath);
    }
  };

  const handleDiscardAndLeave = () => {
    setExitConfirmOpen(false);
    // User chose to throw away their work — clear the saved draft too so it
    // doesn't reappear next time.
    clearAppDraft();
    navigate(applicationsListPath);
  };

  // Edit mode: leave but keep the pending changes as a browser draft (they are
  // restored next time this application is opened for editing).
  const handleKeepDraftAndLeave = () => {
    setExitConfirmOpen(false);
    navigate(applicationsListPath);
  };

  const isBusy = loading || savingSettings || savingDomain;

  const pageTitle = mode === 'edit' ? 'Edit Application' : 'Create Application';
  const pageSubtitle =
    mode === 'edit'
      ? 'Update this application, its domains, and its settings'
      : 'Create and configure a new application for your business';
  const finalButtonLabel = mode === 'edit' ? 'Done' : 'Create Application';

  /* ---------------- Render ---------------- */
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Breadcrumbs sx={{ mb: 0.5, fontSize: 13 }}>
            <MuiLink underline="hover" color="text.secondary" href="#" onClick={handleApplicationsBreadcrumbClick}>
              Applications
            </MuiLink>
            <Typography color="text.primary" fontSize={13} fontWeight={600}>
              {pageTitle}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: getC().textPrimary }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pageSubtitle}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => setPreviewOpen(true)}
          sx={{ borderColor: getC().blue, color: getC().blue, fontWeight: 600, px: 2.5, flexShrink: 0 }}
        >
          Preview
        </Button>
      </Box>

      {draftRestored && (
        <Alert
          severity={staleDraft ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                formik.resetForm();
                setActiveStep(0);
                setDeletedDomains([]);
                setStaleDraft(false);
                clearAppDraft();
              }}
            >
              {mode === 'create' ? 'Start fresh' : 'Discard changes'}
            </Button>
          }
        >
          {mode === 'create'
            ? 'We restored the application details you were working on.'
            : staleDraft
              ? 'Restored unsaved changes from this browser, but this application was modified since they were made. Pressing Done will overwrite those newer changes.'
              : 'Restored unsaved changes from this browser. They will not be saved to the database until you press Done.'}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
          {submitError}
        </Alert>
      )}

      {/* ---------- Stepper ---------- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${getC().border}`,
          p: 3,
          mb: 3,
        }}
      >
        <Grid container spacing={2}>
          {visibleSteps.map((step, index) => (
            <Grid
              item
              xs
              key={step.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRight: index < visibleSteps.length - 1 ? `1px solid ${getC().border}` : 'none',
                pr: 2,
                mr: 2,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                  bgcolor: index === activeStep ? getC().blue : index < activeStep ? getC().green : 'transparent',
                  color: index <= activeStep ? '#fff' : getC().textSecondary,
                  border: index > activeStep ? `2px solid ${getC().border}` : 'none',
                }}
              >
                {index < activeStep ? <CheckCircle sx={{ fontSize: 18 }} /> : index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  noWrap
                  sx={{ color: index === activeStep ? getC().blue : getC().textPrimary }}
                >
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {step.subtitle}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ---------- Step content ---------- */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        <Box sx={{ flex: '1 1 0%', minWidth: 0, width: '100%' }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${getC().border}`, p: 3 }}>
            {currentStepKey === 'details' && (
              <Step1Details
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                errors={errors}
                touched={touched}
                businesses={businesses}
                isAdmin={isAdmin}
                fetching={fetchingBusinesses}
                lockBusiness={mode === 'edit'}
                lockType={mode === 'edit'}
                businessInactive={businessInactive}
              />
            )}
            {currentStepKey === 'domains' && (
              <Step2Domains
                values={values}
                savingDomain={savingDomain}
                onSaveDomain={saveDomain}
                onRemoveDomain={removeDomain}
                onToggleDomain={toggleDomainStatus}
                updateDomainField={updateDomainField}
                addDomainRow={addDomainRow}
                expandedDomains={expandedDomains}
                setExpandedDomains={setExpandedDomains}
                appLogoUrl={values.appLogoUrl}
                domainErrors={domainErrors}
                domainTouched={domainTouched}
                onFieldBlur={markDomainFieldTouched}
              />
            )}
            {currentStepKey === 'settings' && (
              <Step3Security
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                businessInactive={businessInactive}
              />
            )}
            {currentStepKey === 'review' && (
              <Step4Review
                values={values}
                setActiveStep={setActiveStep}
                visibleSteps={visibleSteps}
                createdByEmail={createdByEmail}
                mode={mode}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ borderColor: getC().border, color: getC().textPrimary, px: 3, py: 1 }}
              >
                Back
              </Button>
              {currentStepKey === 'review' ? (
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  sx={{
                    background: `#2563EB`,
                    color: '#fff',
                    px: 5,
                    py: 1.5,
                  }}
                >
                  {submitting ? (mode === 'edit' ? 'Saving…' : 'Creating…') : finalButtonLabel}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isBusy}
                  endIcon={isBusy ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                  sx={{
                    background: `#2563EB`,
                    color: '#fff',
                    px: 5,
                    py: 1.5,
                  }}
                >
                  {isBusy ? 'Processing…' : 'Save & Continue'}
                </Button>
              )}
            </Box>
          </Paper>
        </Box>

      </Box>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Application Preview
          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <PreviewSidebar
            values={values}
            showDomains={showDomains}
            currentStepKey={currentStepKey}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
  open={deleteConfirm.open}
  onClose={() => setDeleteConfirm({ open: false, index: null })}
>
  <DialogTitle>Confirm Delete</DialogTitle>
  <DialogContent>
    <Typography>
      Are you sure you want to delete this domain? This action cannot be undone.
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDeleteConfirm({ open: false, index: null })}>
      Cancel
    </Button>
    <Button onClick={confirmDeleteDomain} color="error" variant="contained">
      Delete
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={exitConfirmOpen}
  onClose={() => !exitSaving && setExitConfirmOpen(false)}
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>{mode === 'create' ? 'Leave without creating?' : 'Leave without pressing Done?'}</DialogTitle>
  <DialogContent>
    <Typography>
      {mode === 'create'
        ? 'This application hasn’t been created yet — nothing has been saved to the database. If you leave now, everything you’ve entered will be discarded.'
        : 'You have changes that haven’t been saved to the database — they are only stored in this browser until you press Done. Keep them as a draft to continue later, or discard them?'}
    </Typography>
  </DialogContent>
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button onClick={() => setExitConfirmOpen(false)} disabled={exitSaving}>
      Cancel
    </Button>
    <Button onClick={handleDiscardAndLeave} color="error" disabled={exitSaving}>
      Discard & Leave
    </Button>
    {mode !== 'create' && (
      <Button onClick={handleKeepDraftAndLeave} variant="contained" disabled={exitSaving}>
        Keep Draft & Leave
      </Button>
    )}
  </DialogActions>
</Dialog>

    </Container>
  );
};

export default ApplicationWizard;